
  import React, { useState, useEffect, useRef } from 'react';
  import { useParams, useNavigate } from 'react-router-dom';
  import { Container, Row, Col, Card, Button, Spinner, Alert, ProgressBar, Modal } from 'react-bootstrap';
  import { FaArrowLeft, FaCheck, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';
  import { BsChevronCompactLeft, BsChevronCompactRight } from 'react-icons/bs';
  import { getLoteLabById, getLotesCalibracionByLoteEquipos } from '../../../api/common';
  import Swal from 'sweetalert2';
  import StepSeleccionPlantilla from './steps/StepSeleccionPlantilla';
  import StepCondicionesAmbientales from './steps/StepCondicionesAmbientales';
  import StepEstabilidad from './steps/StepEstabilidad';
  import StepIBC from './steps/StepIBC';
  import StepSeleccionPatrones from './steps/StepSeleccionPatrones';
  import ResumenFinalizacion from './components/ResumenFinalizacion';
  import ModalCambiarPlantilla from '../lotes/ModalCambiarPlantilla';
  import ModalAjustarPlantillaLote from './components/ModalAjustarPlantillaLote';
  import ValidarPermiso from '../../Roles/ValidarPermiso';
  import { CAMBIAR_PLANTILLA_CONFIRMADA } from '../../Roles/ConstantRoute';
  import { useAuth } from '../../../hooks/useAuth';
  import { useOtCache } from '../../../hooks/useOtCache';


const RegistroMediciones = () => {
    // Determina si se debe saltar el paso de Estabilidad
    const debeSaltarEstabilidad = () => {
      if (!plantillaSeleccionada || !Array.isArray(plantillaSeleccionada.magnitudesConfig)) return false;
      return plantillaSeleccionada.magnitudesConfig.every(m => m.requiereEstabilidad === false);
    } 
  const { id } = useParams(); // id del lote de laboratorio
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [loteEquipos, setLoteEquipos] = useState(null);
  const [lotesCalibracion, setLotesCalibracion] = useState([]);
  const [plantillaSeleccionada, setPlantillaSeleccionada] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [showFinalizarModal, setShowFinalizarModal] = useState(false);
  const [error, setError] = useState(null);
  const [showModalCambiarPlantilla, setShowModalCambiarPlantilla] = useState(false);
  const [showModalAjustarPlantilla, setShowModalAjustarPlantilla] = useState(false);
  const { userData } = useAuth();
  const { invalidate: invalidateOtCache } = useOtCache();
  const puedeCambiarPlantilla = ValidarPermiso(
    CAMBIAR_PLANTILLA_CONFIRMADA,
    userData?.role?.permisos || []
  ) !== -1;

  // Estados para el guardado antes de cambiar de paso
  const [guardandoAntesDeNavegar, setGuardandoAntesDeNavegar] = useState(false);
  const [showModalErrorGuardado, setShowModalErrorGuardado] = useState(false);
  const [accionPendiente, setAccionPendiente] = useState(null); // 'next' | 'prev' | null

  // Refs para cada step con autoguardado.
  // stepPatronesRef → step 1 (StepSeleccionPatrones nuevo, sin autoguardado por ahora — usa botón propio).
  // stepEstabilidadRef → step 2. stepIBCRef → step 3.
  // stepCondicionesFinalRef → step 4 (fix bug: antes no había ref, imposibilitaba guardar antes de navegar).
  const stepEstabilidadRef = useRef(null);
  const stepIBCRef = useRef(null);
  const stepCondicionesFinalRef = useRef(null);

  // Cargar datos iniciales
  useEffect(() => {
    cargarDatos();
  }, [id]);

  const cargarDatos = async () => {
    setLoading(true);
    setError(null);
    try {
      // Cargar lote de laboratorio
      const resLote = await getLoteLabById(id);
      const lote = resLote?.data?.data ?? resLote?.data;
      setLoteEquipos(lote);

      // Verificar si ya existen lotes de calibración creados
      const resLotesCal = await getLotesCalibracionByLoteEquipos(id);
      const lotesCal = resLotesCal?.data?.data ?? resLotesCal?.data ?? [];
      
      if (Array.isArray(lotesCal) && lotesCal.length > 0) {
        setLotesCalibracion(lotesCal);
        setPlantillaSeleccionada(lotesCal[0]?.plantilla);
        // Detectar en qué paso quedó (buscar primer paso incompleto)
        determinarPasoInicial(lotesCal);
      } else {
        // No existen lotes, empezar desde selección de plantilla
        setCurrentStep(0);
      }
    } catch (err) {
      console.error('Error cargando datos:', err);
      setError('Error al cargar los datos del lote. Por favor, intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  // Recargar datos sin cambiar el paso actual (para autoguardado)
  const recargarDatosSinCambiarPaso = async () => {
    try {
      // Cargar lote de laboratorio
      const resLote = await getLoteLabById(id);
      const lote = resLote?.data?.data ?? resLote?.data;
      setLoteEquipos(lote);

      // Verificar si ya existen lotes de calibración creados
      const resLotesCal = await getLotesCalibracionByLoteEquipos(id);
      const lotesCal = resLotesCal?.data?.data ?? resLotesCal?.data ?? [];
      
      if (Array.isArray(lotesCal) && lotesCal.length > 0) {
        setLotesCalibracion(lotesCal);
        setPlantillaSeleccionada(lotesCal[0]?.plantilla);
        // NO llamar a determinarPasoInicial para mantener el paso actual
      }
    } catch (err) {
      console.error('Error recargando datos:', err);
      // No mostrar error al usuario para no interrumpir su trabajo
    }
  };

  // Determinar en qué paso debemos empezar según datos completados.
  // Reescrito tras el fix del paso 1 (spec lotes-paso-patrones-corregido):
  //   paso 1 = selección de patrones (antes cablea mal a condiciones ambientales)
  //   paso 2 = estabilidad
  //   paso 3 = mediciones IBC + patrones
  //   paso 4 = condiciones ambientales finales
  const determinarPasoInicial = (lotesCal) => {
    const primerLote = lotesCal[0];
    const plantilla = primerLote?.plantilla;
    // Prioridad override per-lote → fallback plantilla.
    const cantPatronesReq = (primerLote?.cantPatronesRequeridos ?? plantilla?.cantPatronesRequeridos) || 0;

    // Paso 1: si aún no seleccionó los N patrones principales requeridos, arrancar ahí.
    // Descartar medios de calibración (opcionales) para el chequeo.
    const idsMedios = new Set();
    (plantilla?.magnitudesConfig || []).forEach((m) => {
      const c = m.medioCalibracion;
      if (c) idsMedios.add(String(c?._id ?? c));
    });
    const principales = (primerLote?.patrones || [])
      .filter((p) => !idsMedios.has(String(p?._id ?? p)));
    const patronesOk = cantPatronesReq === 0 || principales.length === cantPatronesReq;
    if (!patronesOk) {
      setCurrentStep(1);
      return;
    }

    // Paso 2: si no hay estabilidad registrada, arrancar ahí.
    const hayEstabilidad = primerLote.sesiones?.some(s =>
      (s.estabilidad || []).some(e => (e.mediciones || []).length > 0)
    );
    if (!hayEstabilidad) {
      setCurrentStep(2);
      return;
    }

    // Si llegamos aquí, ir a mediciones IBC + patrones.
    setCurrentStep(3);
  };

  // Después de crear lotes en bulk
  const onLotesCreados = (lotes, plantilla) => {
    setLotesCalibracion(lotes);
    setPlantillaSeleccionada(plantilla);
    setCurrentStep(1); // Pasar a condiciones ambientales
  };

 
  // Fix bug crítico (spec lotes-paso-patrones-corregido): antes `steps[1].component`
  // era `StepCondicionesAmbientales` — mismatch con el label "Patrones".
  const steps = [
    { label: 'Plantilla', component: StepSeleccionPlantilla, numero: 0 },
    { label: 'Patrones', component: StepSeleccionPatrones, numero: 1 },
    { label: 'Estabilidad', component: StepEstabilidad, numero: 2 },
    { label: 'Mediciones', component: StepIBC, numero: 3 },
    { label: 'Condiciones', component: StepCondicionesAmbientales, numero: 4 }
  ];

  // Validar si se puede avanzar al siguiente paso
  const canGoNext = () => {
    if (currentStep === 0) return false;
    // Paso 1 (Patrones): exige N patrones PRINCIPALES seleccionados. Como
    // `lote.patrones[]` guarda principales + medios de calibración juntos,
    // descartamos los medios (que son opcionales) comparando contra los IDs
    // definidos como `medioCalibracion` en la plantilla.
    if (currentStep === 1) {
      const primerLote = lotesCalibracion[0];
      // Prioridad override per-lote → fallback plantilla.
      const n = (primerLote?.cantPatronesRequeridos ?? plantillaSeleccionada?.cantPatronesRequeridos) || 0;
      if (n === 0) return true;

      const idsMedios = new Set();
      (plantillaSeleccionada?.magnitudesConfig || []).forEach((m) => {
        const c = m.medioCalibracion;
        if (c) idsMedios.add(String(c?._id ?? c));
      });

      const principales = (primerLote?.patrones || [])
        .filter((p) => !idsMedios.has(String(p?._id ?? p)));
      return principales.length === n;
    }
    return currentStep < steps.length - 1;
  };

  const canGoPrevious = () => {
    // No retroceder más allá del paso 1 si ya hay lotes creados
    if (lotesCalibracion.length > 0) {
      return currentStep > 1;
    }
    return currentStep > 0;
  };

  // Obtener el ref del step actual.
  // Paso 1 (StepSeleccionPatrones) NO usa autoguardado por ref — confirma con botón propio.
  // Paso 4 (Condiciones finales) YA tiene ref (bug corregido: antes no lo tenía).
  const getCurrentStepRef = () => {
    if (currentStep === 2) return stepEstabilidadRef;
    if (currentStep === 3) return stepIBCRef;
    if (currentStep === 4) return stepCondicionesFinalRef;
    return null;
  };

  // Guardar antes de navegar
  const guardarAntesDeNavegar = async () => {
    const stepRef = getCurrentStepRef();
    
    // Si no hay ref o no tiene cambios pendientes, no hacer nada
    if (!stepRef || !stepRef.current || !stepRef.current.tieneCambiosPendientes()) {
      return { success: true };
    }

    setGuardandoAntesDeNavegar(true);
    try {
      await stepRef.current.guardarAhora();
      return { success: true };
    } catch (error) {
      console.error('Error guardando antes de navegar:', error);
      return { success: false, error };
    } finally {
      setGuardandoAntesDeNavegar(false);
    }
  };

  const handleNext = async () => {
    if (!canGoNext()) return;

    // Guardar antes de avanzar
    const resultado = await guardarAntesDeNavegar();

    if (resultado.success) {
      setCurrentStep(prev => {
        // Si estamos en el paso 1 y se debe saltar estabilidad, ir a 3
        if (prev === 1 && debeSaltarEstabilidad()) return 3;
        return prev + 1;
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      // Mostrar modal preguntando si quiere continuar sin guardar
      setAccionPendiente('next');
      setShowModalErrorGuardado(true);
    }
  };

  const handlePrevious = async () => {
    if (!canGoPrevious()) return;

    // Guardar antes de retroceder
    const resultado = await guardarAntesDeNavegar();

    if (resultado.success) {
      setCurrentStep(prev => {
        // Si estamos en Mediciones (3) y se debe saltar Estabilidad, volver a Patrones (1)
        if (prev === 3 && debeSaltarEstabilidad()) return 1;
        return prev - 1;
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      // Mostrar modal preguntando si quiere continuar sin guardar
      setAccionPendiente('prev');
      setShowModalErrorGuardado(true);
    }
  };

  // Continuar sin guardar
  const continuarSinGuardar = () => {
    setShowModalErrorGuardado(false);
    
    if (accionPendiente === 'next' && canGoNext()) {
      setCurrentStep(prev => prev + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else if (accionPendiente === 'prev' && canGoPrevious()) {
      setCurrentStep(prev => prev - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    
    setAccionPendiente(null);
  };

  const cancelarNavegacion = () => {
    setShowModalErrorGuardado(false);
    setAccionPendiente(null);
  };

  // Helper: chequea si una sesión tiene AL MENOS una medición NO vacía DEL EQUIPO
  // (per-equipo). Solo mira `medicionesIBC` — NO `medicionesPatrones`, porque en
  // el flujo bulk los patrones son compartidos por todos los lotes hijos (todos
  // los equipos ven la misma medicionesPatrones), así que usarlas como señal de
  // "este equipo tiene datos" es un falso positivo: un patrón lleno haría creer
  // que todos los equipos tienen data aunque uno esté vacío.
  const _sesionTieneDataDeEquipo = (sesion) => {
    return (sesion?.medicionesIBC || []).some((m) => {
      const noVacio = (v) => v !== '' && v !== null && v !== undefined && !Number.isNaN(v);
      if ((m?.mediciones || []).some(noVacio)) return true;
      if ((m?.medicionesSubida || []).some(noVacio)) return true;
      if ((m?.medicionesBajada || []).some(noVacio)) return true;
      return false;
    });
  };

  const handleFinalizarClick = async () => {
    // 1. Validación cruzada del step Condiciones Ambientales (min <= max) —
    //    HARD BLOCK: si min > max, no se puede continuar.
    const stepCA = stepCondicionesFinalRef.current;
    if (stepCA?.esValido && stepCA.esValido() === false) {
      const errs = stepCA.obtenerErrores?.() || {};
      const mensajes = [errs.temperatura, errs.humedad].filter(Boolean).join(' ');
      await Swal.fire({
        title: 'Condiciones ambientales inválidas',
        text: mensajes || 'La temperatura o humedad no cumple el rango.',
        icon: 'error'
      });
      return;
    }

    // Flushear cambios pendientes del step activo (condiciones) antes de validar,
    // y recargar la data fresca de la base para evitar falsos positivos por
    // debounce que aún no disparó.
    try {
      if (stepCA?.tieneCambiosPendientes?.()) {
        await stepCA.guardarAhora?.();
      }
    } catch (e) { console.warn('[handleFinalizarClick] flush CA falló:', e); }

    // Leer lotes de calibración FRESCOS (no del state, que puede estar viejo).
    let lotesFrescos = lotesCalibracion || [];
    try {
      const res = await getLotesCalibracionByLoteEquipos(id);
      const payload = res?.data?.data ?? res?.data ?? [];
      if (Array.isArray(payload) && payload.length > 0) lotesFrescos = payload;
    } catch (e) { console.warn('[handleFinalizarClick] refetch falló:', e); }

    // 2. Warnings blandas — se muestran juntas en un mismo modal y el usuario
    //    puede elegir continuar de todas formas.
    const advertencias = [];

    // 2a. Condiciones ambientales sin registrar (los 4 valores vacíos)
    const primerLote = lotesFrescos?.[0];
    const cond = primerLote?.condicionesAmbientales || {};
    const camposCA = ['temperaturaMin', 'temperaturaMax', 'humedadMin', 'humedadMax'];
    const faltantesCA = camposCA.filter((k) => cond[k] === undefined || cond[k] === null || cond[k] === '');
    if (faltantesCA.length === camposCA.length) {
      advertencias.push('No se registraron <strong>condiciones ambientales</strong> (temperatura ni humedad).');
    } else if (faltantesCA.length > 0) {
      advertencias.push(`Faltan valores de condiciones ambientales: <strong>${faltantesCA.join(', ')}</strong>.`);
    }

    // 2b. Equipos sin mediciones (IBC) del propio equipo en NINGUNA sesión.
    const equiposSinMediciones = (lotesFrescos || [])
      .filter((lote) => !(lote.sesiones || []).some(_sesionTieneDataDeEquipo))
      .map((lote) => {
        const eq = lote.equipoInOt || {};
        const snap = eq.snapshot || {};
        const item = snap.item || eq?.equipo?.item?.nombre || '—';
        const serial = snap.serial || eq?.equipo?.serie || '—';
        return `${item} <span class="text-muted">(S/N: ${serial}, cons: ${eq.consecutivo || '—'})</span>`;
      });
    if (equiposSinMediciones.length > 0) {
      advertencias.push(
        `<strong>${equiposSinMediciones.length} equipo(s) sin mediciones registradas en ninguna sesión:</strong><ul class="text-start mb-0 mt-1">${equiposSinMediciones.map((e) => `<li>${e}</li>`).join('')}</ul>`
      );
    }

    if (advertencias.length === 0) {
      setShowFinalizarModal(true);
      return;
    }

    const result = await Swal.fire({
      title: '¿Continuar con la finalización?',
      html: `
        <div class="text-start">
          <p class="mb-2">Se detectaron los siguientes puntos antes de finalizar:</p>
          <ul class="text-start">${advertencias.map((a) => `<li>${a}</li>`).join('')}</ul>
          <p class="mt-3 text-danger small">
            Una vez finalizado el lote no se puede volver a editar. Verifica que la
            información sea correcta antes de continuar.
          </p>
        </div>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Continuar de todas formas',
      cancelButtonText: 'Revisar antes',
      reverseButtons: true,
      focusCancel: true
    });
    if (result.isConfirmed) setShowFinalizarModal(true);
  };

  if (loading) {
    return (
      <Container className="py-5">
        <div className="text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3 text-muted">Cargando datos del lote...</p>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-5">
        <Alert variant="danger">
          <Alert.Heading>Error</Alert.Heading>
          <p>{error}</p>
          <Button variant="outline-danger" onClick={() => navigate(-1)}>Volver</Button>
        </Alert>
      </Container>
    );
  }

  const StepComponent = steps[currentStep]?.component;
  const showPlantillaStep = currentStep === 0 && lotesCalibracion.length === 0;

  return (
    <Container fluid className="py-4">
      {/* Header */}
      <Card className="shadow-sm mb-4 w-100" style={{borderRadius: '12px', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'}}>
        <Card.Body className="text-white py-3 w-100">
          <Row className="align-items-center">
            <Col>
              <Button variant="light" size="sm" onClick={() => navigate(-1)} className="me-3" style={{borderRadius: '8px'}}>
                <FaArrowLeft className="me-2" /> Volver
              </Button>
              <h4 className="d-inline mb-0">Registro de Mediciones - Lote {loteEquipos?.numeroLote}</h4>
            </Col>
            {plantillaSeleccionada?._id && lotesCalibracion.length > 0 && (
              <Col xs="auto">
                <Button
                  variant="outline-light"
                  size="sm"
                  onClick={() => setShowModalAjustarPlantilla(true)}
                  style={{borderRadius: '8px'}}
                  className="me-2"
                  title="Ajustar los valores de los puntos de calibración de este lote sin cambiar la plantilla original"
                >
                  <i className="bi bi-pencil-square me-1" />
                  Ajustar plantilla del lote
                </Button>
              </Col>
            )}
            {puedeCambiarPlantilla && lotesCalibracion.length > 0 && lotesCalibracion[0]?.estado === 'en_progreso' && (
              <Col xs="auto">
                <Button
                  variant="outline-light"
                  size="sm"
                  onClick={() => setShowModalCambiarPlantilla(true)}
                  style={{borderRadius: '8px'}}
                  title="Cambiar la plantilla actual del lote por otra distinta"
                >
                  <i className="bi bi-arrow-repeat me-1" />
                  Cambiar plantilla
                </Button>
              </Col>
            )}
          </Row>
        </Card.Body>
      </Card>

      {/* Stepper horizontal con navegación integrada */}
      {!showPlantillaStep && (
        <Card className="mb-4 shadow-sm w-100" 
          style={{borderRadius: '12px', 
            position: 'sticky', top: '0', zIndex: 1000, backgroundColor: 'white'}}>
          <Card.Body className="p-4 w-100">
            {/* Desktop: stepper horizontal */}
            <div className="d-none d-md-block">
              <Row className="align-items-center">
                {/* Botón Anterior */}
                <Col xs="auto">
                  <Button
                    variant="outline-secondary"
                    onClick={handlePrevious}
                    disabled={!canGoPrevious()}
                    style={{borderRadius: '8px', padding: '8px 16px'}}
                    title="Paso anterior"
                  >
                    <BsChevronCompactLeft size={24} />
                  </Button>
                </Col>

                {/* Steps */}
                {steps.filter(s => s.numero > 0).map((step, idx) => (
                  <React.Fragment key={step.numero}>
                    <Col className="text-center">
                      <div
                        className={`rounded-circle mx-auto d-flex align-items-center justify-content-center ${
                          currentStep > step.numero ? 'bg-success text-white' :
                          currentStep === step.numero ? 'bg-primary text-white' :
                          'bg-light text-muted'
                        }`}
                        style={{width: '40px', height: '40px', fontWeight: 'bold'}}
                      >
                        {currentStep > step.numero ? <FaCheck /> : step.numero}
                      </div>
                      <div className={`mt-2 small ${currentStep === step.numero ? 'text-primary fw-bold' : 'text-muted'}`}>
                        {step.label}
                      </div>
                    </Col>
                    {idx < steps.filter(s => s.numero > 0).length - 1 && (
                      <Col xs={1} className="px-0">
                        <div className="border-top border-2 border-secondary" style={{marginTop: '-20px'}}></div>
                      </Col>
                    )}
                  </React.Fragment>
                ))}

                {/* Botón Siguiente/Finalizar */}
                <Col xs="auto">
                  {currentStep === steps.length - 1 ? (
                    <Button
                      variant="success"
                      onClick={handleFinalizarClick}
                      style={{borderRadius: '8px', padding: '8px 16px'}}
                      disabled={loteEquipos?.estado === 'cerrado'}
                      title={loteEquipos?.estado === 'cerrado' ? 'Lote ya procesado' : 'Finalizar lote'}
                    >
                      <FaCheckCircle size={20} />
                    </Button>
                  ) : (
                    <Button
                      variant="primary"
                      onClick={handleNext}
                      disabled={!canGoNext()}
                      style={{borderRadius: '8px', padding: '8px 16px'}}
                      title="Siguiente paso"
                    >
                      <BsChevronCompactRight size={24} />
                    </Button>
                  )}
                </Col>
              </Row>
            </div>

            {/* Mobile: indicador simple con botones */}
            <div className="d-md-none">
              <div className="text-center mb-3">
                <h6 className="mb-2">Paso {currentStep} de {steps.length - 1}</h6>
                <ProgressBar now={(currentStep / (steps.length - 1)) * 100} variant="primary" />
              </div>
              <Row>
                <Col>
                  <Button
                    variant="outline-secondary"
                    onClick={handlePrevious}
                    disabled={!canGoPrevious()}
                    size="sm"
                    style={{borderRadius: '8px'}}
                  >
                    <BsChevronCompactLeft size={20} /> Anterior
                  </Button>
                </Col>
                <Col className="text-end">
                  {currentStep === steps.length - 1 ? (
                    <Button
                      variant="success"
                      onClick={handleFinalizarClick}
                      size="sm"
                      style={{borderRadius: '8px'}}
                      disabled={loteEquipos?.estado === 'cerrado'}
                      title={loteEquipos?.estado === 'cerrado' ? 'Lote ya procesado' : 'Finalizar lote'}
                    >
                      <FaCheckCircle className="me-1" /> {loteEquipos?.estado === 'cerrado' ? 'Procesado' : 'Finalizar'}
                    </Button>
                  ) : (
                    <Button
                      variant="primary"
                      onClick={handleNext}
                      disabled={!canGoNext()}
                      size="sm"
                      style={{borderRadius: '8px'}}
                    >
                      Siguiente <BsChevronCompactRight size={20} />
                    </Button>
                  )}
                </Col>
              </Row>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Contenido del paso actual */}
      <Card className="shadow-sm mb-4 w-100" style={{borderRadius: '12px'}}>
        <Card.Body className="p-4 w-100">
          {/* Spinner de guardado */}
          {guardandoAntesDeNavegar && (
            <Alert variant="info" className="d-flex align-items-center gap-2 mb-3">
              <Spinner animation="border" size="sm" />
              <span>Guardando cambios antes de continuar...</span>
            </Alert>
          )}
          
          {showPlantillaStep ? (
            <StepSeleccionPlantilla
              loteEquipos={loteEquipos}
              onLotesCreados={onLotesCreados}
            />
          ) : (
            <>
              {currentStep === 1 && (
                <StepSeleccionPatrones
                  loteEquipos={loteEquipos}
                  lotesCalibracion={lotesCalibracion}
                  plantilla={plantillaSeleccionada}
                  onDataChange={recargarDatosSinCambiarPaso}
                />
              )}
              {currentStep === 2 && (
                <StepEstabilidad
                  ref={stepEstabilidadRef}
                  loteEquipos={loteEquipos}
                  lotesCalibracion={lotesCalibracion}
                  plantilla={plantillaSeleccionada}
                  onDataChange={recargarDatosSinCambiarPaso}
                />
              )}
              {currentStep === 3 && (
                <StepIBC
                  ref={stepIBCRef}
                  loteEquipos={loteEquipos}
                  lotesCalibracion={lotesCalibracion}
                  plantilla={plantillaSeleccionada}
                  onDataChange={recargarDatosSinCambiarPaso}
                />
              )}
              {currentStep === 4 && (
                <StepCondicionesAmbientales
                  ref={stepCondicionesFinalRef}
                  loteEquipos={loteEquipos}
                  lotesCalibracion={lotesCalibracion}
                  plantilla={plantillaSeleccionada}
                  onDataChange={recargarDatosSinCambiarPaso}
                />
              )}
            </>
          )}
        </Card.Body>
      </Card>

      {/* Modal de error al guardar */}
      <Modal show={showModalErrorGuardado} onHide={cancelarNavegacion} centered>
        <Modal.Header closeButton className="bg-warning">
          <Modal.Title>
            <FaExclamationTriangle className="me-2" />
            Error al guardar
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>No se pudieron guardar los cambios pendientes antes de cambiar de paso.</p>
          <p className="mb-0">¿Desea continuar de todas formas? Los cambios no guardados se perderán.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={cancelarNavegacion}>
            Cancelar
          </Button>
          <Button variant="warning" onClick={continuarSinGuardar}>
            Continuar sin guardar
          </Button>
        </Modal.Footer>
      </Modal>



      {/* Modal de finalización */}
      {showFinalizarModal && (
        <ResumenFinalizacion
          show={showFinalizarModal}
          onHide={() => setShowFinalizarModal(false)}
          loteEquiposId={id}
          lotesCalibracion={lotesCalibracion}
          plantilla={plantillaSeleccionada}
          onSuccess={() => { invalidateOtCache(); navigate(-1); }}
        />
      )}

      {/* Modal de cambio de plantilla (spec lotes-cambio-plantilla-post-confirmacion) */}
      <ModalCambiarPlantilla
        show={showModalCambiarPlantilla}
        onHide={() => setShowModalCambiarPlantilla(false)}
        loteId={lotesCalibracion[0]?._id}
        plantillaActualId={lotesCalibracion[0]?.plantilla?._id || lotesCalibracion[0]?.plantilla}
        tieneMediciones={(lotesCalibracion || []).some(lc =>
          (lc.sesiones || []).some(s =>
            (s.medicionesIBC || []).some(m => (m.mediciones || []).length > 0 || (m.medicionesSubida || []).length > 0 || (m.medicionesBajada || []).length > 0) ||
            (s.medicionesPatrones || []).some(m => (m.mediciones || []).length > 0 || (m.medicionesSubida || []).length > 0 || (m.medicionesBajada || []).length > 0) ||
            (s.estabilidad || []).some(e => (e.mediciones || []).length > 0)
          )
        )}
        onPlantillaCambiada={() => cargarDatos()}
      />

      {/* Modal para ajustar puntos de calibración por magnitud (sin cambiar plantilla) */}
      <ModalAjustarPlantillaLote
        show={showModalAjustarPlantilla}
        onHide={() => setShowModalAjustarPlantilla(false)}
        lotesCalibracion={lotesCalibracion}
        plantilla={plantillaSeleccionada}
        onSaved={() => cargarDatos()}
      />
    </Container>
  );
};

export default RegistroMediciones;
